import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator } from '@microservices/wiskey-react-components'
import { Box, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { CommandParameters } from '@components/CommandParameters'
import { GeneralButtons } from '@components/GeneralButtons'

import { PrintFormButton } from '@features/print-forms'

import { TYPES_SOURCE_OPTIONS } from '@constants'

import { PrintFormTemplateEditors } from './components/PrintFormTemplateEditors'
import { useInputs, usePrintFormCreateOrEdit } from './hooks'

export const PrintFormCreateOrEdit = () => {
  const { t } = useTranslation()
  const {
    methods,
    isEdit,
    commandReady,
    watchObjectCode,
    watchCommandCode,
    objectsOptions,
    selectedObjectCommandsOptions,
    handleChangeCommand,
    handleSave,
    handleCancel,
    previewPrintConfig,
    canPrintPreview,
  } = usePrintFormCreateOrEdit()

  const { generalInputs, commandInputs } = useInputs({
    isEdit,
    objectsOptions,
    selectedObjectCommandsOptions,
    handleChangeCommand,
  })

  return (
    <FormProvider {...methods}>
      <PageContentLayout sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            <Typography mb={1} variant='h5'>
              {t(isEdit ? 'printForms.editHeader' : 'printForms.createHeader')}
            </Typography>

            <FormGenerator inputs={generalInputs} />

            <Box component='fieldset' mb={2} mt={2} position='relative'>
              <legend>{t('printForms.commandSection')}</legend>
              <FormGenerator inputs={commandInputs} />
              {commandReady && (
                <>
                  <CommandParameters
                    disableFieldType
                    commandCode={watchCommandCode!.id as string}
                    containerSx={{ mb: 1 }}
                    label={t('printForms.commandParams')}
                    labelWidth={120}
                    name='command.parameters'
                    parametersMode='params'
                    sourceCode={watchObjectCode!.id as string}
                    sourceTitle={watchObjectCode!.label}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />
                  <CommandParameters
                    disableFieldType
                    commandCode={watchCommandCode!.id as string}
                    containerSx={{ mb: 1 }}
                    label={t('printForms.commandBody')}
                    labelWidth={120}
                    name='command.body'
                    parametersMode='body'
                    sourceCode={watchObjectCode!.id as string}
                    sourceTitle={watchObjectCode!.label}
                    typeSource={TYPES_SOURCE_OPTIONS.OBJECT}
                  />
                </>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography mb={1} variant='h6'>
                {t('printForms.templateSection')}
              </Typography>
              <PrintFormTemplateEditors />
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              background: 'white',
              borderTop: '1px solid',
              borderColor: 'grey.300',
              boxShadow: '0 -1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                pt: 1,
                pb: 1,
                pr: 1,
                pl: 1,
              }}
            >
              <PrintFormButton
                config={previewPrintConfig}
                isDisabled={!canPrintPreview}
                label={t('printForms.print')}
              />
              <GeneralButtons
                cancelText={t('printForms.cancel')}
                saveText={t('printForms.save')}
                onCancel={handleCancel}
                onSave={handleSave}
              />
            </Box>
          </Box>
        </Box>
      </PageContentLayout>
    </FormProvider>
  )
}
